import React, { useState, useEffect, forwardRef } from 'react';
import { Modal, Form, Input, Radio, Button, message, Select, Divider } from 'antd';
import { useAuth } from '../../../auth/AuthContext';
import dayjs from 'dayjs';
import ReactDatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import InputMask from 'react-input-mask';
import './EditPatientModal'

// Custom input component with react-input-mask for YYYY-MM-DD format (using a normal HTML input)
// Now accepts a disabled prop and passes it to the underlying input.
const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder, disabled }, ref) => (
  <InputMask
    mask="9999-99-99"
    value={value || ''}
    onChange={onChange}
    onClick={onClick}
    placeholder={placeholder}
    disabled={disabled}
    ref={ref}
  >
    {(inputProps) => <input {...inputProps} disabled={disabled} />}
  </InputMask>
));

// DatePickerField that integrates react-datepicker with the custom input
const DatePickerField = ({ value, onChange, disabled }) => {
  return (
    <ReactDatePicker
      selected={value}
      onChange={(date) => onChange(date)}
      dateFormat="yyyy-MM-dd"
      placeholderText="YYYY-MM-DD"
      customInput={<CustomDateInput disabled={disabled} />}
      disabled={disabled}
    />
  );
};

const EditPatientModal = ({ open, setOpen, patientId, setUpdated }) => {
  const auth = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [quickNotesEditing, setQuickNotesEditing] = useState(false);
  const [addingNewQuickNote, setAddingNewQuickNote] = useState(false);

  useEffect(() => {
    if (patientId && open) {
      fetchPatientDetails(patientId);
    }
  }, [patientId, open]);

  const fetchPatientDetails = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/patients/${id}`, {
        headers: {
          'Authorization': `Bearer ${auth.user['access_token']}`,
        },
      });
      if (response.ok) {
        const result = await response.json();

        // Process the date of birth: convert to a JS Date object if valid
        const dob = dayjs(result.date_of_birth, 'YYYY-MM-DD');
        if (dob.isValid()) {
          form.setFieldsValue({ date_of_birth: dob.toDate() });
        } else {
          console.error("Invalid date format:", result.date_of_birth);
        }

        // Set other fields
        form.setFieldsValue({
          first_name: result.first_name,
          last_name: result.last_name,
          gender: result.gender,
          email: result.email,
          phone: result.phone,
        });

        // Process notes if available
        if (result.notes && Array.isArray(result.notes) && result.notes.length > 0) {
          // Sort notes by created_at descending (latest first)
          const sortedNotes = result.notes.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setNotes(sortedNotes);
          setSelectedNoteId(sortedNotes[0].id);
          form.setFieldsValue({ quick_patient_notes: sortedNotes[0].note });
        } else {
          form.setFieldsValue({ quick_patient_notes: '' });
          setNotes([]);
          setSelectedNoteId(null);
        }
      } else {
        const errorData = await response.json();
        message.error(errorData.detail?.message || 'Failed to fetch patient details');
      }
    } catch (error) {
      message.error('Error fetching patient details');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format a date string in EST
  const formatDateInEST = (dateString) => {
    const dateObj = new Date(dateString);
    const dateInEST = dateObj.toLocaleDateString('en-CA', {
      timeZone: 'America/New_York',
    });
    const timeInEST = dateObj.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour12: false,
    });
    return `${dateInEST} ${timeInEST} (EST)`;
  };

  // Handler for enabling main edit mode
  const handleEdit = () => {
    setQuickNotesEditing(false);
    setAddingNewQuickNote(false);
    setIsEditable(true);
  };

  const handleOk = () => {
    form.validateFields()
      .then(async (values) => {
        setLoading(true);

        // Exclude quick_patient_notes from the main update payload
        const { quick_patient_notes, ...otherValues } = values;

        // Trim string values and format date
        const formattedValues = {
          ...otherValues,
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          email: values.email ? values.email.trim() : null,
          phone: values.phone ? values.phone.trim() : null,
          date_of_birth: values.date_of_birth ? dayjs(values.date_of_birth).format('YYYY-MM-DD') : null,
        };

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/patients/${patientId}/update`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${auth.user['access_token']}`,
            },
            method: 'PATCH',
            body: JSON.stringify(formattedValues),
          });

          if (response.ok) {
            message.success('Patient details updated successfully');
            setIsEditable(false);
            setOpen(false);
            setUpdated && setUpdated(true);
          } else {
            const errorData = await response.json();
            message.error(errorData.detail?.message || 'Failed to update patient details');
          }
        } catch (error) {
          message.error('Failed to update patient');
          console.error('Error submitting patient data:', error);
        } finally {
          setLoading(false);
        }
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
        setLoading(false);
      });
  };

  // Handler for updating an existing quick note
  const handleQuickNotesUpdate = async () => {
    const noteText = form.getFieldValue('quick_patient_notes');
    if (!selectedNoteId) {
      message.error('No note selected to update.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/patients/note/${patientId}/${selectedNoteId}?note=${encodeURIComponent(noteText)}`,
        {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          method: 'PUT',
        }
      );
      if (response.ok) {
        message.success('Quick notes updated successfully');
        setNotes(prevNotes =>
          prevNotes.map((note) =>
            note.id === selectedNoteId ? { ...note, note: noteText } : note
          )
        );
        setQuickNotesEditing(false);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail?.message || 'Failed to update quick notes');
      }
    } catch (error) {
      message.error('Failed to update quick notes');
      console.error('Error updating quick notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handler for adding a new quick note
  const handleAddNewQuickNote = async () => {
    const noteText = form.getFieldValue('quick_patient_notes');
    if (!noteText) {
      message.error('Please enter a quick note.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/patients/note/${patientId}?note=${encodeURIComponent(noteText)}`,
        {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          method: 'POST',
        }
      );
      if (response.ok) {
        message.success('New quick note added successfully');
        await fetchPatientDetails(patientId);
        setAddingNewQuickNote(false);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail?.message || 'Failed to add new quick note');
      }
    } catch (error) {
      message.error('Failed to add new quick note');
      console.error('Error adding quick note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setIsEditable(false);
    setQuickNotesEditing(false);
    setAddingNewQuickNote(false);
    form.resetFields();
  };

  return (
    <Modal
      title="Edit Patient Details"
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      centered
      width={800}
      footer={[
        <Button
          key="edit"
          onClick={handleEdit}
          disabled={isEditable || quickNotesEditing || addingNewQuickNote}
        >
          Edit Basic Patient Details
        </Button>,
        <Button
          key="editQuickNotes"
          type={quickNotesEditing ? 'primary' : 'default'}
          onClick={() => {
            if (quickNotesEditing) {
              handleQuickNotesUpdate();
            } else {
              setIsEditable(false);
              setAddingNewQuickNote(false);
              setQuickNotesEditing(true);
            }
          }}
          disabled={isEditable || addingNewQuickNote || !notes.length}
        >
          {quickNotesEditing ? 'Save Edited Notes' : 'Edit Previous Notes'}
        </Button>,
        <Button
          key="addNewQuickNotes"
          type={addingNewQuickNote ? 'primary' : 'default'}
          onClick={() => {
            if (addingNewQuickNote) {
              handleAddNewQuickNote();
            } else {
              setIsEditable(false);
              setQuickNotesEditing(false);
              setAddingNewQuickNote(true);
              form.setFieldsValue({ quick_patient_notes: '' });
            }
          }}
          disabled={isEditable || quickNotesEditing}
        >
          {addingNewQuickNote ? 'Save New Note' : 'Add New Note'}
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleOk}
          disabled={!isEditable}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" name="edit_patient_form">
        <Form.Item
          name="first_name"
          label="First Name"
          rules={[{ required: true, message: 'Please input the first name!' }]}
        >
          <Input placeholder="Enter first name" disabled={!isEditable} />
        </Form.Item>

        <Form.Item
          name="last_name"
          label="Last Name"
          rules={[{ required: true, message: 'Please input the last name!' }]}
        >
          <Input placeholder="Enter last name" disabled={!isEditable} />
        </Form.Item>

        <Form.Item name="date_of_birth" label="Date of Birth (YYYY-MM-DD)">
          {/* Use our custom DatePickerField instead of antd DatePicker */}
          <DatePickerField disabled={!isEditable} />
        </Form.Item>

        <Form.Item name="gender" label="Gender">
          <Radio.Group disabled={!isEditable}>
            <Radio value="M">Male</Radio>
            <Radio value="F">Female</Radio>
            <Radio value="O">Other</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="email" label="Email">
          <Input placeholder="Enter email" disabled={!isEditable} />
        </Form.Item>

        <Form.Item name="phone" label="Phone">
          <PhoneInput
            country={'us'}
            value={form.getFieldValue('phone')}
            onChange={(value) => form.setFieldsValue({ phone: value.trim() })}
            disabled={!isEditable}
            inputStyle={{ width: '100%' }}
          />
        </Form.Item>

        <Divider />

        {/* Quick Notes Section */}
        {!addingNewQuickNote && notes.length > 0 && (
          <Form.Item label="Select Note Date & Time (For Previous Notes)">
            <Select
              showSearch
              placeholder="Select a note timestamp"
              value={selectedNoteId}
              onChange={(value) => {
                setSelectedNoteId(value);
                const selectedNote = notes.find((note) => note.id === value);
                if (selectedNote) {
                  form.setFieldsValue({ quick_patient_notes: selectedNote.note });
                }
              }}
              disabled={!quickNotesEditing}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={notes.map((note) => ({
                value: note.id,
                label: formatDateInEST(note.created_at),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item name="quick_patient_notes" label="Quick Patient Notes">
          <Input.TextArea
            placeholder="Enter any quick note about the patient"
            rows={4}
            allowClear
            disabled={!(quickNotesEditing || addingNewQuickNote)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPatientModal;
